import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Standard screen layout for myams.
 *
 * Why a wrapper instead of putting overflowY: auto on .ma-screen?
 *
 * .ma-screen is the positioning context for `<MaBottomNav>`, which is
 * `position: absolute; bottom: 0`. If .ma-screen itself scrolls, "bottom: 0"
 * anchors to the END of the scrollable content (i.e. scrolls along with the
 * content) instead of the visible viewport. The result is a nav bar that
 * sits in the middle of the page when there's enough content. Reproduced
 * locally with `preview_inspect`: nav at y=864 inside an 812-tall screen.
 *
 * Fix: .ma-screen is `overflow: hidden`, an inner div does the scrolling,
 * and the footer (typically <MaBottomNav>) is a non-scrolling sibling of
 * the scroll container.
 *
 * Also: scroll position is preserved across navigation. When the user
 * presses Back, we restore where they were. New PUSH navigations start
 * at the top.
 *
 * Pull-to-refresh (optional, via onPullToRefresh):
 * Detects a downward drag from scrollTop = 0; shows a spinner / hint
 * that translates with the gesture, calls the async handler on release
 * past the threshold. Direct DOM mutation during the drag, no React
 * re-renders per pointermove.
 */

// Module-level cache so positions survive route remounts.
const scrollCache = new Map<string, number>();

const PULL_THRESHOLD = 70;     // px of vertical pull to commit a refresh
const PULL_MAX = 110;          // hard ceiling so the indicator can't fly off

interface Props {
  children: ReactNode;
  footer?: ReactNode;
  /** Extra bottom padding inside the scroll area so content clears the footer. */
  padBottom?: number;
  scrollStyle?: CSSProperties;
  /** Disable scroll-position memory for this route (e.g. always-fresh feeds). */
  noScrollMemory?: boolean;
  /** When set, enables pull-to-refresh: dragging down from scrollTop=0
   *  past ~70 px and releasing fires the handler. The handler can
   *  return a promise — the spinner stays visible until it resolves. */
  onPullToRefresh?: () => Promise<unknown> | void;
}

export function Screen({ children, footer, padBottom = 86, scrollStyle, noScrollMemory = false, onPullToRefresh }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [refreshing, setRefreshing] = useState(false);
  // Keep latest handler + refreshing flag in refs so the native touch
  // listener (set up once in useEffect) always sees current values
  // without re-binding.
  const handlerRef = useRef(onPullToRefresh);
  const refreshingRef = useRef(refreshing);
  handlerRef.current = onPullToRefresh;
  refreshingRef.current = refreshing;
  const location = useLocation();
  const navType = useNavigationType();
  const key = location.pathname + location.search;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Restore: only on POP (browser back/forward). PUSH / REPLACE start at top.
    if (!noScrollMemory && navType === "POP") {
      const saved = scrollCache.get(key);
      el.scrollTop = saved ?? 0;
    } else {
      el.scrollTop = 0;
    }

    return () => {
      // React keeps the element in the DOM through the cleanup phase, so
      // el.scrollTop here is the value at the moment of unmount. No need
      // for a separate scroll listener.
      if (!noScrollMemory && el.isConnected) scrollCache.set(key, el.scrollTop);
    };
  }, [key, navType, noScrollMemory]);

  // ─── Pull-to-refresh ──────────────────────────────────────────────────
  //
  // Direct DOM transforms during the drag so we don't re-render the
  // whole screen at 60 Hz. We translate `innerRef` (the scroll content)
  // AND `indicatorRef` (the "release to refresh" hint) by the same px,
  // so they slide together. The indicator sits at -PULL_THRESHOLD by
  // default, off-screen above the scroll container, and crosses zero
  // when the user has pulled enough to commit.
  const applyPull = (px: number, transition = "none") => {
    const inner = innerRef.current;
    const ind = indicatorRef.current;
    if (inner) {
      inner.style.transition = transition;
      inner.style.transform = `translateY(${px}px)`;
    }
    if (ind) {
      ind.style.transition = transition;
      ind.style.transform = `translateY(${px}px)`;
      ind.style.opacity = String(Math.min(1, px / PULL_THRESHOLD));
    }
  };

  // Native touch listeners with passive: false on touchmove so we can
  // call preventDefault when the gesture is committed to pulling.
  // React's synthetic touch events are passive (a perf default it
  // can't override), and iOS Safari in PWA standalone mode hands
  // ownership of the gesture to native scroll the moment our handler
  // doesn't call preventDefault — pointermove then stops firing.
  // Going below React directly is the only reliable cross-iOS approach.
  useEffect(() => {
    if (!onPullToRefresh) return;
    const scroll = ref.current;
    if (!scroll) return;

    let startY = 0;
    let active = false;
    let touching = false;

    const onStart = (e: TouchEvent) => {
      if (refreshingRef.current) return;
      if (scroll.scrollTop > 0) return;
      if (e.touches.length !== 1) return;
      startY = e.touches[0]!.clientY;
      touching = true;
      active = false;
    };

    const onMove = (e: TouchEvent) => {
      if (!touching) return;
      const dy = e.touches[0]!.clientY - startY;
      if (!active) {
        if (scroll.scrollTop > 0) { touching = false; return; }
        if (dy < 8) return;
        active = true;
      }
      if (dy <= 0) {
        active = false;
        applyPull(0);
        return;
      }
      // We've claimed the gesture — block the browser's native scroll
      // for the rest of this move so we don't compete with momentum.
      e.preventDefault();
      applyPull(Math.min(dy * 0.6, PULL_MAX));
    };

    const onEnd = (e: TouchEvent) => {
      if (!touching) return;
      touching = false;
      if (!active) { applyPull(0); return; }
      active = false;
      const dy = (e.changedTouches[0]?.clientY ?? 0) - startY;
      const pulled = Math.min(dy * 0.6, PULL_MAX);
      const handler = handlerRef.current;
      if (pulled >= PULL_THRESHOLD && handler) {
        setRefreshing(true);
        applyPull(PULL_THRESHOLD, "transform 220ms ease, opacity 220ms ease");
        Promise.resolve(handler()).catch(() => { /* non-fatal */ })
          .finally(() => {
            setRefreshing(false);
            applyPull(0, "transform 240ms ease, opacity 240ms ease");
          });
      } else {
        applyPull(0, "transform 220ms ease, opacity 220ms ease");
      }
    };

    // touchmove MUST be passive:false so preventDefault is honoured.
    scroll.addEventListener("touchstart", onStart, { passive: true });
    scroll.addEventListener("touchmove", onMove, { passive: false });
    scroll.addEventListener("touchend", onEnd, { passive: true });
    scroll.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      scroll.removeEventListener("touchstart", onStart);
      scroll.removeEventListener("touchmove", onMove);
      scroll.removeEventListener("touchend", onEnd);
      scroll.removeEventListener("touchcancel", onEnd);
    };
    // We only depend on whether pull is enabled at all; the actual
    // handler + refreshing state are read via refs above.
  }, [onPullToRefresh]);

  return (
    <div className="ma-screen">
      {/* Pull-to-refresh indicator. Absolutely positioned at the top of
          the screen, off-screen above the scroll area by default. Slides
          into view as the user pulls. */}
      {onPullToRefresh && (
        <div
          ref={indicatorRef}
          style={{
            position: "absolute",
            top: -PULL_THRESHOLD,
            left: 0, right: 0,
            height: PULL_THRESHOLD,
            display: "flex", alignItems: "center", justifyContent: "center",
            pointerEvents: "none",
            zIndex: 5, opacity: 0,
            color: "var(--ma-ink-soft)",
            fontFamily: "var(--ma-mono)", fontSize: 11,
            letterSpacing: "0.14em", textTransform: "uppercase",
          }}
        >
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "8px 14px",
            background: "var(--ma-cream)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
          }}>
            <span style={{
              display: "inline-block",
              width: 10, height: 10, borderRadius: "50%",
              border: "2px solid var(--ma-ink)",
              borderTopColor: "transparent",
              animation: refreshing ? "maSpin 0.8s linear infinite" : "none",
            }} />
            {refreshing ? "refreshing…" : "release to refresh"}
          </span>
        </div>
      )}

      <div
        ref={ref}
        style={{
          position: "absolute",
          inset: 0,
          overflowY: "auto",
          overflowX: "hidden",
          paddingBottom: footer ? padBottom : 0,
          WebkitOverflowScrolling: "touch",
          // Contain overscroll so the iOS native rubber-band bounce
          // doesn't compete with our PTR for the first few px of pull.
          overscrollBehavior: onPullToRefresh ? "contain" : undefined,
          ...scrollStyle,
        }}
      >
        <div ref={innerRef} style={{ willChange: onPullToRefresh ? "transform" : undefined }}>
          {children}
        </div>
      </div>
      {footer}
    </div>
  );
}
